import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, cardSx, panelHeaderSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, fmtBtc, fmtTimeFromEpoch, truncHash, engineStatusMap } from '../utils';

function MiningOverview({ engineInfo, liveState }) {
  const t = useDashboardTheme();
  const rewardsBoards = liveState?.rewardsBoards || [];
  const rewards = engineInfo?.rewards || [];

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDBox>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
            Mining Overview
          </MDTypography>
          <MDTypography variant='caption' sx={{ color: t.secondaryText, fontSize: '0.7rem' }}>
            Live engine state
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <MDBox display='flex' gap={1} flexWrap='wrap' mb={1.5}>
          <Chip
            label={(engineStatusMap[engineInfo.status] || { label: engineInfo.status }).label}
            color={(engineStatusMap[engineInfo.status] || { color: 'default' }).color}
            size='small'
            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
          />
          {engineInfo.coins?.map((c) => (
            <Chip key={c} label={c} variant='outlined' size='small' sx={{ fontSize: '0.65rem' }} />
          ))}
        </MDBox>

        <MDBox display='flex' flexDirection='column' gap={0.5} mb={2}>
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Hash Budget Remaining: <strong style={{ color: t.primaryText }}>{fmtBtc(Math.max(0, (Number(engineInfo.availableAmount) || 0) - (Number(engineInfo.paidAmount) || 0)))} BTC</strong>
            <span style={{ opacity: 0.5 }}> / {fmtBtc(engineInfo.availableAmount)} locked</span>
          </MDTypography>
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Airdrop Pot: <strong style={{ color: t.primaryText }}>{fmtBtc(engineInfo.airdropAmount)} BTC</strong>
          </MDTypography>
        </MDBox>

        {/* Rewards Boards (cumulative) */}
        {rewardsBoards.length > 0 && (
          <MDBox mb={2}>
            <MDTypography variant='subtitle2' fontWeight='bold' mb={0.5} sx={{ fontSize: '0.85rem', color: t.primaryText }}>
              Cumulative Rewards
            </MDTypography>
            {rewardsBoards.slice().sort((a, b) => (Number(b.amountUsd) || 0) - (Number(a.amountUsd) || 0)).map((rb, i) => (
              <MDTypography key={i} variant='caption' display='block' sx={{ color: t.secondaryText }}>
                {rb.currency}: {fmtBtc(rb.amountBtc)} BTC / ${fmt(rb.amountUsd)}
              </MDTypography>
            ))}
          </MDBox>
        )}

        {/* Recent engine rewards */}
        {rewards.length > 0 && (
          <MDBox>
            <MDTypography variant='subtitle2' fontWeight='bold' mb={0.5} sx={{ fontSize: '0.85rem', color: t.primaryText }}>
              Recent Engine Rewards ({rewards.length})
            </MDTypography>
            <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 200 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow sx={tableHeadRowSx(t)}>
                    <TableCell>Time</TableCell>
                    <TableCell>Coin</TableCell>
                    <TableCell align='right'>Amount</TableCell>
                    <TableCell align='right'>BTC</TableCell>
                    <TableCell align='right'>USD</TableCell>
                    <TableCell>Block</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rewards
                    .slice()
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, 20)
                    .map((r, i) => (
                      <TableRow key={i} sx={tableRowSx(t, { index: i })}>
                        <TableCell>
                          <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtTimeFromEpoch(r.timestamp)}</MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{r.currency}</MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(r.amount)}</MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>{fmtBtc(r.amountBtc)}</MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(r.amountUsd)}</MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText, fontFamily: 'monospace' }}>{truncHash(r.blockHash)}</MDTypography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

export default MiningOverview;
